'use client'

import React, { useEffect, useCallback, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { cn } from 'lib/utils'
import styles from './index.module.scss'

interface SkeletonProductCardProps {
  isCenter?: boolean
}

const SkeletonProductCard: React.FC<SkeletonProductCardProps> = ({ isCenter }) => {
  return (
    <div className={`${styles.bgColor} relative h-full overflow-hidden transition-all duration-300 z-10`}>
      {/* Avatar skeleton */}
      <div
        className={`${
          isCenter ? 'absolute' : 'hidden'
        } overflow-hidden w-[50px] sm:w-[30px] h-[50px] sm:h-[30px] top-4 left-4 z-50`}
      >
        <div className="w-full h-full rounded-full skeletonLoader" />
      </div>

      {/* Product image skeleton */}
      <div className="overflow-hidden w-[300px] h-[400px]">
        <div className="w-full h-full skeletonLoader" />
      </div>

      {/* Bottom section skeleton - only for center card */}
      {isCenter && (
        <div className={styles.bottomSection}>
          <div>
            <div className="w-20 h-4 skeletonLoader mb-1 mx-auto rounded" />
            <div className="w-12 h-3 skeletonLoader mx-auto rounded" />
          </div>
        </div>
      )}
    </div>
  )
}

export const DiscoveryFeedSkeleton: React.FC = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    loop: true,
    dragFree: false,
    startIndex: 0,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const selectedSnap = emblaApi.selectedScrollSnap()
    setSelectedIndex(selectedSnap)
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    const duration = 4000 // 4 seconds
    const startTime = Date.now()

    const updateProgress = () => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min((elapsed / duration) * 100, 100)

      if (newProgress >= 100) {
        emblaApi.scrollNext()
      }
    }

    const interval = setInterval(updateProgress, 16) // ~60fps

    return () => clearInterval(interval)
  }, [emblaApi, selectedIndex])

  const skeletonProducts = Array.from({ length: 10 }, (_, index) => ({
    id: `skeleton-${index}`,
  }))

  return (
    <div className="flex items-center justify-center w-full h-full relative" style={{ userSelect: 'none' }}>
      {/* Main Carousel */}
      <div className="w-full overflow-hidden py-[72px] sm:py-32 lg:py-[160px] 2xl:py-[160px]" ref={emblaRef}>
        <div className="flex">
          {skeletonProducts.map((product, index) => {
            const isCenter = index === selectedIndex // Center the middle item

            // Calculate z-index: center is highest, then decreases as distance increases
            const count = skeletonProducts.length
            let distanceFromCenter = Math.abs(index - selectedIndex)

            // Handle circular distance for proper z-index calculation
            const circularDistance = Math.min(distanceFromCenter, count - distanceFromCenter)

            // Calculate z-index: center gets highest value, decreases with distance
            const zIndex = isCenter ? 50 : Math.max(50 - circularDistance * 5, 1)

            return (
              <div
                key={`skeleton-${index}`}
                className={`flex-[0_0_300px] sm:flex-[0_0_300px] min-w-0 mx-0`}
                style={{
                  zIndex,
                }}
              >
                <div
                  className={cn(
                    'relative group transition-all duration-500 ease-out transform origin-center',
                    isCenter ? 'scale-[1.18] sm:scale-[1.5]' : 'scale-[1.08] sm:scale-[1.3]'
                  )}
                >
                  <div
                    className={`absolute -inset-x-[-5px] ${
                      !isCenter ? 'inset-y-[25px]' : 'inset-y-[45px]'
                    } bg-black/50 rounded-md z-0`}
                    style={{ boxShadow: '0px 0px 15px 15px rgba(0, 0, 0, 0.6)' }}
                  />
                  <SkeletonProductCard isCenter={isCenter} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
